import React, { useContext, useState } from 'react';
import { PlugFill, X } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { darkTheme } from '../../theme/themes';
import PrimaryButton from '../shared/PrimaryButton';
import ConnectBrokerModal from './ConnectBrokerModal';
import DisconnectBrokerModal from './DisconnectBrokerModal';
import {
  formatBrokerConnectionLabel,
  formatBrokerDisplayName,
  isBrokerLinked,
  isTradestationDirectBroker,
} from '../../utils/brokerConnection';

/**
 * Sidebar footer / Settings broker controls: connection list + Connect / Add Broker.
 */
export default function BrokerConnectionAction({ variant = 'sidebar' }) {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const [showConnect, setShowConnect] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);

  const connected = isBrokerLinked(user);
  const connections = (() => {
    const list = user?.brokerConnections?.length
      ? [...user.brokerConnections]
      : user?.activeBrokerConnection
        ? [user.activeBrokerConnection]
        : [];
    // Client-side guard: Direct credentials with only CT rows in brokerConnections.
    const hasDirect = list.some((c) => c?.adapter_type === 'tradestation_direct');
    if (!hasDirect && isTradestationDirectBroker(user)) {
      list.push({
        id: 'legacy',
        adapter_type: 'tradestation_direct',
        broker_name: 'TRADESTATION',
        connection_id: null,
        status: 'active',
      });
    }
    return list;
  })();

  const wrapperPadding = variant === 'sidebar' ? '0' : '6px 14px 10px';
  const isSidebar = variant === 'sidebar';
  const isSettings = variant === 'settings';
  const connectButtonLabel = connected ? 'Add Broker' : 'Connect Broker';
  // Sidebar chrome stays dark in light + dark themes — keep broker labels light.
  const sidebarBrokerText = darkTheme.text;

  const openConnect = () => setShowConnect(true);

  const connectButtonStyle = isSidebar
    ? {
        width: '100%',
        borderRadius: 8,
        minWidth: 0,
        background: `linear-gradient(135deg, ${colors.purpleLight || colors.primaryLight}, ${colors.purple || colors.primary})`,
        boxShadow: `0 2px 8px ${colors.purple || colors.primary}33`,
      }
    : {
        alignSelf: 'flex-start',
        borderRadius: 8,
        minWidth: 190,
        background: `linear-gradient(135deg, ${colors.purpleLight || colors.primaryLight}, ${colors.purple || colors.primary})`,
        boxShadow: `0 2px 8px ${colors.purple || colors.primary}33`,
      };

  return (
    <>
      <div
        style={{
          width: '100%',
          padding: wrapperPadding,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: isSidebar ? 8 : 6,
        }}
      >
        {isSettings ? (
          <>
            <div
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: 10,
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  gap: 12,
                  padding: '10px 12px',
                  background: colors.bgCard,
                  borderBottom: `1px solid ${colors.border}`,
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.35px',
                }}
              >
                <div>Broker</div>
                <div>Action</div>
              </div>

              {connections.length > 0 ? (
                connections.map((conn, idx) => (
                  <div
                    key={conn.id || conn.connection_id || `${formatBrokerConnectionLabel(conn)}-${idx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: 12,
                      alignItems: 'center',
                      padding: '12px',
                      borderBottom: idx < connections.length - 1 ? `1px solid ${colors.border}` : 'none',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>
                        {formatBrokerDisplayName(conn?.broker_name)}
                      </div>
                      <div
                        style={{
                          color: colors.textMuted,
                          fontSize: 12,
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={formatBrokerConnectionLabel(conn) || ''}
                      >
                        {formatBrokerConnectionLabel(conn)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDisconnectTarget(conn)}
                      style={{
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.text,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        minWidth: 92,
                      }}
                    >
                      Disconnect
                    </button>
                  </div>
                ))
              ) : (
                <div style={{ padding: '14px 12px', fontSize: 13, color: colors.textMuted }}>
                  No connected brokers.
                </div>
              )}
            </div>

            <PrimaryButton
              type="button"
              size="sm"
              onClick={openConnect}
              style={connectButtonStyle}
            >
              <PlugFill size={14} />
              {connectButtonLabel}
            </PrimaryButton>
          </>
        ) : (
          <>
            {connections.length > 0 && (
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: '0 2px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {connections.map((conn, idx) => {
                  const name = formatBrokerConnectionLabel(conn) || formatBrokerDisplayName(conn?.broker_name);
                  const canDisconnect = Boolean(conn?.id) && conn.id !== 'legacy';
                  return (
                    <li
                      key={conn.id || conn.connection_id || `${name}-${idx}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        minWidth: 0,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: colors.success,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          color: sidebarBrokerText,
                          fontSize: 13,
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={name}
                      >
                        {name}
                      </span>
                      {canDisconnect && (
                        <button
                          type="button"
                          onClick={() => setDisconnectTarget(conn)}
                          title={`Disconnect ${name}`}
                          aria-label={`Disconnect ${name}`}
                          style={{
                            flexShrink: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 22,
                            height: 22,
                            padding: 0,
                            border: 'none',
                            borderRadius: 6,
                            background: 'transparent',
                            color: '#ef4444',
                            cursor: 'pointer',
                            lineHeight: 1,
                          }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <PrimaryButton
              type="button"
              size="sm"
              onClick={openConnect}
              style={connectButtonStyle}
            >
              <PlugFill size={14} />
              {connectButtonLabel}
            </PrimaryButton>
          </>
        )}
      </div>

      <ConnectBrokerModal show={showConnect} onHide={() => setShowConnect(false)} />
      <DisconnectBrokerModal
        show={!!disconnectTarget}
        onHide={() => setDisconnectTarget(null)}
        connection={disconnectTarget}
      />
    </>
  );
}
